/**
 * Centralized Axios client with interceptors for:
 * - Authentication
 * - Rate limiting with automatic retry
 * - Error handling with RFC 9457 Problem format
 * - Request/response logging in development
 */

import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/lib/env';

// Create axios instance with base configuration
export const apiClient = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for authentication
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add auth token if available, but don't override explicitly set Authorization
    // (e.g., public trial setup uses its own access token)
    if (!config.headers?.Authorization) {
      const token = localStorage.getItem('authToken');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Navigation guard to prevent double redirects
let isNavigating = false;

// Response interceptor for error handling and rate limiting
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized with specific reasons
    if (error.response?.status === 401) {
      // Don't redirect if already on login page or if already navigating
      if (window.location.pathname.includes('/login') || isNavigating) {
        return Promise.reject(error);
      }

      // Don't redirect for credential validation errors (wrong password / bad TOTP code).
      // These are "your credentials are wrong" — not "your session expired".
      // Callers (login, review dialog, password change) handle them inline.
      const problemData = error.response.data as any;
      const problemType: string = problemData?.type || '';
      const isCredentialError =
        problemType.includes('InvalidCredentials') || problemType.includes('InvalidTwoFactorCode');

      if (isCredentialError) {
        return Promise.reject(error);
      }

      // Set navigation flag to prevent double redirects
      isNavigating = true;

      let redirectUrl = '/login';

      // Check for specific auth failure reasons
      if (problemData?.detail || problemData?.instance) {
        const detail = problemData.detail?.toLowerCase() || '';
        const instance = problemData.instance || '';

        // Token was explicitly blacklisted/revoked
        if (detail.includes('revoked') || instance.includes('blacklist')) {
          console.warn('🔒 Token was revoked - user logged out from another device or by admin');
          redirectUrl = '/login?reason=revoked';
        }
        // Token expired or malformed
        else if (
          detail.includes('expired') ||
          detail.includes('malformed') ||
          instance.includes('decode')
        ) {
          console.info('⏰ Token expired - session timeout');
          redirectUrl = '/login?reason=expired';
        }
        // User no longer exists
        else if (detail.includes('no longer exists') || instance.includes('user-lookup')) {
          console.warn('👤 User account no longer exists');
          redirectUrl = '/login?reason=account-removed';
        }
      }

      localStorage.removeItem('authToken');

      // Use setTimeout to reset flag after navigation completes
      setTimeout(() => {
        isNavigating = false;
      }, 1000);

      window.location.href = redirectUrl;
      return Promise.reject(error);
    }

    // Handle 429 Rate Limiting with automatic retry
    if (error.response?.status === 429 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Get retry-after from header or problem response
      const retryAfterHeader = error.response.headers['retry-after'];
      const problemData = error.response.data as any;
      const retryAfterSeconds = retryAfterHeader
        ? parseInt(retryAfterHeader, 10)
        : problemData?.extensions?.retry_after || 60; // Default to 60 seconds

      // Emit custom event for UI components to listen to
      window.dispatchEvent(
        new CustomEvent('rateLimit', {
          detail: {
            retryAfter: retryAfterSeconds,
            endpoint: originalRequest.url,
          },
        })
      );

      // Don't auto-retry - let the user decide when to try again
      return Promise.reject(error);
    }

    // Parse Problem format errors (RFC 9457).
    // axios 1.18 types a header value as string | number | boolean | string[] |
    // AxiosHeaders, so guard for string before calling .includes().
    const contentType = error.response?.headers['content-type'];
    if (
      error.response &&
      typeof contentType === 'string' &&
      contentType.includes('application/problem+json')
    ) {
      const problem = error.response.data as Problem;

      // Create enhanced error with problem details
      const enhancedError = new ProblemError(problem);
      return Promise.reject(enhancedError);
    }

    return Promise.reject(error);
  }
);

/**
 * RFC 9457 Problem interface
 */
export interface Problem {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  extensions?: Record<string, any>;
}

/**
 * Enhanced error class for Problem responses
 */
export class ProblemError extends Error {
  constructor(public problem: Problem) {
    super(problem.detail || problem.title);
    this.name = 'ProblemError';
  }

  get isPermissionError(): boolean {
    return this.problem.type.includes('InsufficientPermissions') || this.problem.status === 403;
  }

  get isValidationError(): boolean {
    return this.problem.status === 422 || this.problem.status === 400;
  }

  get isRateLimit(): boolean {
    return this.problem.type.includes('RateLimitExceeded') || this.problem.status === 429;
  }

  get isNotFound(): boolean {
    return this.problem.status === 404;
  }

  get isConflict(): boolean {
    return this.problem.status === 409;
  }

  get retryAfter(): number | undefined {
    return this.problem.extensions?.retry_after;
  }
}
